import React, { CSSProperties, DragEventHandler, ReactNode } from 'react';
import { DndProvider, useDrag, useDragLayer, useDrop } from 'react-dnd';
import { HTML5Backend, getEmptyImage } from 'react-dnd-html5-backend';

/**
 * This class was copied from foundation to facilitate easier improvements and refactoring.
 * Once the necessary changes are complete, it will be moved back to foundation.
 * Improvements:
 *  - added DragLayer
 *  - added type to Droppable
 *  - added selectedItems, hideDragPreview props to Draggable
 *  - added the type for functions props, to know the `entityName`
 * 
 */

export declare type Identifier = string | symbol;

export type CommonProps = {
    key?: number | string;
    className?: string;
    height?: number;
    item?: any;
    type?: Identifier;
    children?: ReactNode;
}

export type DraggableProps = CommonProps & {
    selectedItems?: any[];
    onDraggingStyle?: React.CSSProperties;
    onDragStart?: DragEventHandler<any>;
    canDrag?: (selectedItems: any[], entityType: Identifier) => boolean;
    hideDragPreview?: boolean;
}

export const Draggable = (props: DraggableProps) => {
    const [{ isDragging }, drag, dragPreview] = useDrag(() => ({
        type: props.type ?? 'draggable',
        item: {
            ...props.item,
            selectedItems: props.selectedItems ?? (props.item ? [props.item] : [])
        },
        canDrag: props.canDrag ? props.canDrag(props.selectedItems ?? (props.item ? [props.item] : []), props.type ?? 'draggable') : true,
        collect: monitor => ({
            isDragging: !!monitor.isDragging() && !props.hideDragPreview,
        }),
        options: {
            dropEffect: "copy"
        }
    }), [props.item, props.selectedItems]);

    if (props.hideDragPreview) {
        dragPreview(getEmptyImage());
    }

    const style = isDragging ? props.onDraggingStyle : undefined;
    return <div ref={drag} style={{ ...style, height: props.height, opacity: isDragging ? 0.5 : 1, cursor: 'grab' }} key={props.key} className={props.className}
        onDragStart={props.onDragStart}>
        {props.children}
    </div >

}

export type DroppableProps = CommonProps & {
    accept?: Identifier | Identifier[];
    item?: any;
    children?: JSX.Element | JSX.Element[];
    onOverStyle?: React.CSSProperties;
    canDrop?: (sources: any[], sourcesEntityType: Identifier, dest: any, destEntityType: Identifier) => boolean;
    drop?: (sources: any[], sourcesEntityType: Identifier, dest: any, destEntityType: Identifier) => void;
}

// KI: https://github.com/react-dnd/react-dnd/issues/3530
export const Droppable = (props: DroppableProps) => {
    const [{ isOver, canDrop }, drop] = useDrop(
        () => ({
            accept: props.accept ?? 'draggable',
            type: props.type ?? 'droppable',
            item: props.item,
            drop: (item: any, monitor: any) => props.drop && props.drop(item.selectedItems,monitor.getItemType(), props.item, props.type ?? 'draggable'),
            canDrop: (item: any, monitor: any) => {
                return props.canDrop ? props.canDrop(item.selectedItems, monitor.getItemType(), props.item, props.type ?? 'droppable') : true;
            },
            collect: (monitor) => ({
                isOver: !!monitor.isOver(),
                canDrop: !!monitor.canDrop()
            })
        })
    );

    const style = isOver ? props.onOverStyle : undefined;
    return <div ref={drop} style={{ ...style, height: props.height, transition: '0.1s' }} key={props.key} className={props.className}>
        {props.children}
    </div >;
}

export type DragLayerProps = {
    renderLayer: (selectedItems: any, entityType: Identifier) => ReactNode;
};

const DragLayer = (props: DragLayerProps) => {
    const { isDragging, item, currentOffset, itemType } = useDragLayer(monitor => ({
        item: monitor.getItem(),
        itemType: monitor.getItemType(),
        currentOffset: monitor.getClientOffset(),
        isDragging: monitor.isDragging(),
    }));

    if (!isDragging || !currentOffset) {
        return null;
    }

    const { x, y } = currentOffset;
    const transform = `translate(${x}px, ${y}px)`;
    const itemStyles: CSSProperties = { transform, WebkitTransform: transform };
    const layerStyles: CSSProperties = {
        position: 'fixed',
        pointerEvents: 'none',
        zIndex: 1000,
        left: 0,
        top: 0,
    };

    return (
        <div style={layerStyles}>
            <div style={itemStyles}>
                <div style={{ padding: '6px', border: '2px solid lightblue', background: '#e0f7fa', borderRadius: '3px', opacity: 0.9 }}>
                    {props.renderLayer(item.selectedItems, itemType!)}
                </div>
            </div>
        </div>
    );
};

export type DragAndDropAreaProps = Partial<DragLayerProps>;

export class DragAndDropArea extends React.Component<DragAndDropAreaProps> {
    render() {
        return <DndProvider backend={HTML5Backend}>
            {this.props.renderLayer && <DragLayer renderLayer={this.props.renderLayer} />}
            {this.props.children}
        </DndProvider>
    }
}
